import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { css, cx } from "@emotion/css";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import Button from "../../../../shared/ui/button/button";
import SectionTitle from "../../../../shared/ui/section-title/section-title";
// Local hero images
import pianoKey from "../../../../assets/images/piano-key.jpg";
import swanLake from "../../../../assets/images/swan-lake.jpg";
import pianoGrandmaster from "../../../../assets/images/the-piano-grandmaster.jpg";

interface SlideInfo {
  uuid: string;
  title: string;
  dayLocation: string;
  description: string;
  image?: string;
  align: string;
}

const slidesInfo: SlideInfo[] = [
  // Make Ballet the first slide
  {
    uuid: "f3c4e36f-8f0e-4505-8fde-c182434d5a73",
    title: "Ballet",
    dayLocation: "A definir",
    description: `Prepare-se para um mundo de romance e fantasia com as deslumbrantes 
    coreografias dos clássicos de Tchaikovsky em "Once Upon A Time: Ballet".`,
    image: swanLake,
    align: "center",
  },
  // Classical Piano Music: day and location to be defined
  {
    uuid: "5dd79d16-3772-44f0-8450-b66c8b860d34",
    title: "Classical Piano Music",
    dayLocation: "A definir",
    description: `Nostalgia de tempos em que não vivemos. 
    "Once Upon A Time: Classical Piano Music" nos traz um repertório sublime 
    das mais belas obras do piano clássico.`,
    image: pianoKey,
    align: "center",
  },
  {
    uuid: "6846cbb0-72d0-445d-bed1-5c8bc8a4ccec",
    title: "The Piano Grandmaster",
    dayLocation: "A definir",
    description: `Uma viagem de volta ao século XIX: as mais emocionantes e consagradas obras de Frédéric Chopin,
    em "Once Upon A Time: The Piano Grandmaster".`,
    image: pianoGrandmaster,
    align: "30% 10%",
  },
];

const carouselWrapper = css({
  position: "relative",
  width: "100%",
});

const navigationWrapper = css({
  position: "relative",
});

const carousel = css({
  overflow: "hidden",
});

const carouselEvent = css({
  position: "relative",
  height: "31.25rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  color: "var(--primary-color)",
  "@media (max-width: 768px)": {
    height: "50vh",
    minHeight: "400px",
  },
});

const contentContainer = css({
  width: "100%",
  maxWidth: "90rem",
  margin: "0 auto",
  padding: "var(--spacing-2xl) calc(var(--spacing-2xl) + 52px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  "@media (max-width: 768px)": {
    padding: "var(--spacing-lg) calc(var(--spacing-lg) + 42px)",
  },
  "@media (max-width: 480px)": {
    padding: "var(--spacing-md) calc(var(--spacing-md) + 34px)",
  },
});

const eventInfo = css({
  maxWidth: "45em",
  zIndex: 2,
});

const eventTitle = css({
  fontSize: "var(--fs-800)",
  fontWeight: 700,
  marginBottom: "var(--spacing-xl)",
  "@media (max-width: 768px)": {
    fontSize: "var(--fs-700)",
    marginBottom: "var(--spacing-lg)",
  },
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-600)",
    marginBottom: "var(--spacing-md)",
  },
});

const eventDayLocation = css({
  fontSize: "var(--fs-650)",
  fontWeight: 500,
  lineHeight: 1.2,
  paddingBottom: "var(--spacing-lg)",
  color: "rgba(var(--pcrgba-100), 1)",
  "@media (max-width: 768px)": {
    fontSize: "var(--fs-550)",
    paddingBottom: "var(--spacing-md)",
  },
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-500)",
    paddingBottom: "var(--spacing-sm)",
  },
});

const eventDescription = css({
  fontSize: "var(--fs-400)",
  lineHeight: 1.6,
  marginBottom: "var(--spacing-xl)",
  opacity: 0.9,
  "@media (max-width: 768px)": {
    fontSize: "var(--fs-350)",
    marginBottom: "var(--spacing-lg)",
  },
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-300)",
    marginBottom: "var(--spacing-md)",
  },
});

const eventButton = css({
  minWidth: "220px",
  padding: "var(--spacing-md) var(--spacing-xl)",
  fontSize: "var(--fs-450)",
  backgroundColor: "var(--primary-color)",
  color: "var(--secondary-color)",
  border: "1px solid var(--primary-color)",
  textTransform: "uppercase",
  "&:hover": {
    backgroundColor: "rgba(var(--primary-color-rgb), 0.9)",
  },
  "@media (max-width: 768px)": {
    fontSize: "var(--fs-400)",
    minWidth: "160px",
    padding: "var(--spacing-sm) var(--spacing-md)",
  },
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-350)",
    minWidth: "130px",
    padding: "var(--spacing-xs) var(--spacing-sm)",
  },
});

const arrowBase = css({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: "48px",
  height: "48px",
  backgroundColor: "rgba(var(--pcrgba-900), 0.8)",
  border: "none",
  borderRadius: "50%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(var(--pcrgba-800), 0.9)",
  },
  "&:disabled": {
    opacity: 0.3,
    cursor: "not-allowed",
  },
  "& svg": {
    width: "20px",
    height: "20px",
    fill: "var(--primary-color)",
  },
  "@media (max-width: 768px)": {
    width: "40px",
    height: "40px",
    "& svg": {
      width: "16px",
      height: "16px",
    },
  },
  "@media (max-width: 480px)": {
    width: "32px",
    height: "32px",
    "& svg": {
      width: "14px",
      height: "14px",
    },
  },
});

const arrowLeft = css({
  left: "var(--spacing-sm)",
  "@media (max-width: 768px)": {
    left: "var(--spacing-xs)",
  },
  "@media (max-width: 480px)": {
    left: "var(--spacing-2xs)",
  },
});

const arrowRight = css({
  right: "var(--spacing-sm)",
  "@media (max-width: 768px)": {
    right: "var(--spacing-xs)",
  },
  "@media (max-width: 480px)": {
    right: "var(--spacing-2xs)",
  },
});

const dots = css({
  position: "absolute",
  bottom: "var(--spacing-lg)",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "var(--spacing-xs)",
  zIndex: 10,
});

const dot = css({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  border: "none",
  backgroundColor: "rgba(var(--primary-color-rgb), 0.4)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(var(--primary-color-rgb), 0.7)",
  },
});

const dotActive = css({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  border: "none",
  backgroundColor: "var(--primary-color)",
  cursor: "pointer",
});

interface ArrowProps {
  left?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

export interface EventsHeroBannerProps extends React.HTMLAttributes<HTMLDivElement> {}

const Arrow: React.FC<ArrowProps> = ({ left, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cx(arrowBase, left ? arrowLeft : arrowRight)}
    >
      <svg viewBox="0 0 24 24">
        {left ? (
          <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
        ) : (
          <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
        )}
      </svg>
    </button>
  );
};

export const EventsHeroBanner: React.FC<EventsHeroBannerProps> = ({ className, ...rest }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider({
    slides: {
      perView: 1,
    },
    loop: true,
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const hasDefinedDate = (dayLocation: string) => {
    return !dayLocation.includes("A definir");
  };

  const getSlideBackground = (slide: SlideInfo) => {
    if (slide.image) {
      return `linear-gradient(135deg, rgba(var(--pcrgba-800), 1) 0%, rgba(var(--pcrgba-700), 0.5) 60%, rgba(var(--pcrgba-600), 0.35) 100%), url(${slide.image})`;
    }
    return `linear-gradient(135deg, rgba(var(--pcrgba-800), 1) 0%, rgba(var(--pcrgba-700), 1) 60%, rgba(var(--pcrgba-600), 1) 100%)`;
  };

  return (
    <div className={cx(carouselWrapper, className)} {...rest}>
      <div className={navigationWrapper}>
        <div ref={sliderRef} className={cx(carousel, "keen-slider")}>
          {slidesInfo.map((slide) => (
            <div
              key={slide.uuid}
              className={cx(carouselEvent, "keen-slider__slide")}
              style={{
                backgroundImage: getSlideBackground(slide),
                backgroundPosition: slide.align,
              }}
            >
              <div className={contentContainer}>
                <div className={eventInfo}>
                  <SectionTitle level={2} className={eventTitle}>{slide.title}</SectionTitle>
                  <p className={eventDayLocation}>{slide.dayLocation}</p>
                  <p className={eventDescription}>{slide.description}</p>
                  <Button
                    className={eventButton}
                    onClick={() => navigate(`/evento/${slide.uuid}`)}
                  >
                    {hasDefinedDate(slide.dayLocation)
                      ? "Comprar Ingressos"
                      : "Saiba mais"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {loaded && instanceRef.current && (
          <>
            <Arrow
              left
              onClick={() => instanceRef.current?.prev()}
              disabled={!instanceRef.current.options.loop && currentSlide === 0}
            />
            <Arrow
              onClick={() => instanceRef.current?.next()}
              disabled={
                !instanceRef.current.options.loop &&
                currentSlide ===
                  instanceRef.current.track.details.slides.length - 1
              }
            />
          </>
        )}
      </div>

      {loaded && instanceRef.current && (
        <div className={dots}>
          {Array.from({
            length: instanceRef.current.track.details.slides.length,
          }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => instanceRef.current?.moveToIdx(idx)}
              className={currentSlide === idx ? dotActive : dot}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsHeroBanner;
